/**
 * DOM helpers. Kept tiny on purpose — just enough to build semantic
 * HTML without the fog of `document.createElement` repetition.
 */

type AttrValue = string | number | boolean | null | undefined | EventListener;
type Attrs = Record<string, AttrValue>;
type Child = Node | string | null | undefined | false;

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs | null = null,
  children: Child | Child[] = [],
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === null || v === undefined || v === false) continue;
      if (k === "class") el.className = String(v);
      else if (k.startsWith("on") && typeof v === "function") {
        el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
      } else if (k === "dataset") {
        // no-op; use data-* directly
      } else if (v === true) el.setAttribute(k, "");
      else el.setAttribute(k, String(v));
    }
  }
  const arr = Array.isArray(children) ? children : [children];
  for (const c of arr) {
    if (c === null || c === undefined || c === false) continue;
    el.append(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return el;
}

export function on<K extends keyof HTMLElementEventMap>(
  el: HTMLElement,
  type: K,
  handler: (ev: HTMLElementEventMap[K]) => void,
): void {
  el.addEventListener(type, handler);
}

export function $(sel: string, root: ParentNode = document): HTMLElement {
  const found = root.querySelector(sel);
  if (!found) throw new Error(`Missing element: ${sel}`);
  return found as HTMLElement;
}

export function empty(el: HTMLElement): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Text span that clamps to `lines` lines by default. Click toggles
 * between clamped and fully expanded. Native `title` carries the full
 * text for hover tooltips.
 */
export function clamp(text: string, lines = 2): HTMLElement {
  return h(
    "span",
    {
      class: "clamp",
      title: text,
      role: "button",
      tabindex: 0,
      "data-clamp-lines": String(lines),
      "aria-expanded": "false",
    },
    text,
  );
}

/**
 * Renders a diff-engine `humanLabel` with value-level truncation.
 *
 * The label is tokenised into plain-text runs + embedded *values*
 * (quoted names and file paths). Each value renders as a small
 * clickable pill: short form by default, full on click, native tooltip
 * with the full value. Plain text is untouched.
 *
 * Short forms:
 *   - name   → `'first N chars…'` (N ≈ 28)
 *   - path   → `…/filename`  (filename truncated too if over M chars)
 */
const NAME_SHORT_MAX = 28;
const FILENAME_SHORT_MAX = 32;
const PATH_SEPARATOR_RE = /[/\\]/;

type Segment =
  | { kind: "text"; value: string }
  | { kind: "name"; value: string }
  | { kind: "path"; value: string }
  | { kind: "color"; value: string } // `#rrggbb` or `#rrggbbaa`
  | { kind: "arrow"; value: string }; // `→` between before/after values

export function renderLabel(label: string): HTMLElement {
  const segments = segmentLabel(label);
  const root = document.createElement("span");
  root.className = "label";
  for (const seg of segments) {
    root.appendChild(renderSegment(seg));
  }
  return root;
}

function renderSegment(seg: Segment): Node {
  if (seg.kind === "text") {
    return document.createTextNode(seg.value);
  }
  if (seg.kind === "color") {
    return renderColorSegment(seg.value);
  }
  if (seg.kind === "arrow") {
    return h("span", { class: "arrow", "aria-hidden": "true" }, seg.value);
  }
  const [short, full] = seg.kind === "name"
    ? formatName(seg.value)
    : formatPath(seg.value);
  if (short === full) {
    return document.createTextNode(full);
  }
  return h(
    "span",
    {
      class: `val val--${seg.kind}`,
      title: full,
      role: "button",
      tabindex: 0,
      "aria-expanded": "false",
      "data-short": short,
      "data-full": full,
    },
    short,
  );
}

/**
 * Colour chip: fills the background with the hex colour, picks a
 * contrasting text colour via the YIQ heuristic (simple, fast, matches
 * what most UI libraries do for small text over a brand colour).
 */
function renderColorSegment(hex: string): HTMLElement {
  const chip = h("span", { class: "val val--color", title: hex }, hex);
  // Inline style — the colour is per-value and unpredictable, so a
  // stylesheet rule would be wrong. Keeps the renderer pure.
  chip.style.background = hex;
  chip.style.color = pickTextColor(hex);
  return chip;
}

/**
 * Pick black or white text depending on the background's perceived
 * brightness. Uses the YIQ formula (0.299·R + 0.587·G + 0.114·B).
 */
function pickTextColor(hex: string): "#000" | "#fff" {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return "#fff";
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#000" : "#fff";
}

/** Name (single-quoted). Always keeps the outer `'...'` quotes. */
function formatName(name: string): [short: string, full: string] {
  const full = `'${name}'`;
  if (name.length <= NAME_SHORT_MAX) return [full, full];
  return [`'${name.slice(0, NAME_SHORT_MAX - 1)}…'`, full];
}

/** Path: keep the filename; replace the directory prefix with `…/`. */
function formatPath(path: string): [short: string, full: string] {
  const sepIdx = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
  if (sepIdx < 0) {
    // No separator — treat as a name-ish.
    if (path.length <= FILENAME_SHORT_MAX) return [path, path];
    return [`${path.slice(0, FILENAME_SHORT_MAX - 1)}…`, path];
  }
  const filename = path.slice(sepIdx + 1);
  const shownFilename = filename.length <= FILENAME_SHORT_MAX
    ? filename
    : `${filename.slice(0, FILENAME_SHORT_MAX - 1)}…`;
  // If the whole path already fits, don't clip.
  if (path.length <= FILENAME_SHORT_MAX && filename === shownFilename) {
    return [path, path];
  }
  return [`…/${shownFilename}`, path];
}

/**
 * Split a label into alternating text / value segments. Recognises:
 *   - `'NAME'` quoted names
 *   - `(sample: PATH)` — path lives between `sample: ` and the matching
 *     closing `)`. Paths can contain spaces + parens, so we can't
 *     regex-match them without this anchor.
 */
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}(?:[0-9a-fA-F]{2})?(?![0-9a-fA-F])/;

function segmentLabel(text: string): Segment[] {
  const PATH_PREFIX = "(sample: ";
  const segs: Segment[] = [];
  let i = 0;
  let textStart = 0;
  const pushText = (end: number) => {
    if (end > textStart) segs.push({ kind: "text", value: text.slice(textStart, end) });
  };

  while (i < text.length) {
    if (text[i] === "'") {
      const end = text.indexOf("'", i + 1);
      if (end > i) {
        pushText(i);
        segs.push({ kind: "name", value: text.slice(i + 1, end) });
        i = end + 1;
        textStart = i;
        continue;
      }
    }
    if (text.startsWith(PATH_PREFIX, i)) {
      // Find the closing `)` that terminates the (sample: ...) group.
      // Sample paths themselves may contain parentheses (e.g. " (1)"),
      // so we scan to the *last* `)` before the end or next opening token.
      const rest = text.slice(i + PATH_PREFIX.length);
      const end = rest.lastIndexOf(")");
      if (end >= 0) {
        pushText(i);
        segs.push({ kind: "text", value: PATH_PREFIX });
        segs.push({ kind: "path", value: rest.slice(0, end) });
        segs.push({ kind: "text", value: ")" });
        i = i + PATH_PREFIX.length + end + 1;
        textStart = i;
        continue;
      }
    }
    if (text[i] === "#") {
      const m = HEX_COLOR_RE.exec(text.slice(i));
      if (m) {
        pushText(i);
        segs.push({ kind: "color", value: m[0] });
        i += m[0].length;
        textStart = i;
        continue;
      }
    }
    if (text[i] === "→") {
      pushText(i);
      segs.push({ kind: "arrow", value: "→" });
      i++;
      textStart = i;
      continue;
    }
    i++;
  }
  pushText(text.length);
  return segs;
}

/**
 * Install a single document-level click + keydown delegate that
 * toggles `.clamp` elements between clamped and expanded. Idempotent.
 */
let clampDelegateInstalled = false;
export function installClampDelegate(): void {
  if (clampDelegateInstalled) return;
  clampDelegateInstalled = true;

  const toggleClamp = (el: HTMLElement): void => {
    const open = el.classList.toggle("clamp--open");
    el.setAttribute("aria-expanded", String(open));
  };

  const toggleVal = (el: HTMLElement): void => {
    const open = el.classList.toggle("val--open");
    el.setAttribute("aria-expanded", String(open));
    const short = el.dataset.short;
    const full = el.dataset.full;
    if (short && full) {
      el.textContent = open ? full : short;
    }
  };

  const resolveTarget = (
    ev: Event,
  ): { el: HTMLElement; kind: "clamp" | "val" } | null => {
    const t = ev.target;
    if (!(t instanceof HTMLElement)) return null;
    const val = t.closest<HTMLElement>(".val");
    // Colour swatches are display-only; no toggle behaviour.
    if (val && !val.classList.contains("val--color")) {
      return { el: val, kind: "val" };
    }
    const clampEl = t.closest<HTMLElement>(".clamp");
    if (clampEl) return { el: clampEl, kind: "clamp" };
    return null;
  };

  document.addEventListener("click", (ev) => {
    const hit = resolveTarget(ev);
    if (!hit) return;
    if (hit.kind === "val") toggleVal(hit.el);
    else toggleClamp(hit.el);
  });

  document.addEventListener("keydown", (ev) => {
    if (ev.key !== "Enter" && ev.key !== " ") return;
    const hit = resolveTarget(ev);
    if (!hit) return;
    ev.preventDefault();
    if (hit.kind === "val") toggleVal(hit.el);
    else toggleClamp(hit.el);
  });
}
